import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash, Pencil, FilePdf, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const daysBetween = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(1, Math.ceil((e - s) / (1000 * 60 * 60 * 24)));
};

export const GanttChart = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ task_name: '', trade_id: '', start_date: '', end_date: '', progress: 0, notes: '' });
  const [dprTasks, setDprTasks] = useState([]);

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    try {
      const [tasksRes, tradesRes, dprRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/gantt-tasks`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/gantt-tasks/dpr-progress`, { withCredentials: true }),
      ]);
      setTasks(tasksRes.data);
      setTrades(tradesRes.data);
      setDprTasks(dprRes.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ task_name: '', trade_id: '', start_date: '', end_date: '', progress: 0, notes: '' }); setEditId(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.task_name || !form.start_date || !form.end_date) { toast.error('Fill required fields'); return; }
    try {
      if (editId) {
        await axios.put(`${BACKEND_URL}/api/gantt-tasks/${editId}`, form, { withCredentials: true });
        toast.success('Task updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/projects/${projectId}/gantt-tasks`, form, { withCredentials: true });
        toast.success('Task created');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleEdit = (t) => {
    setForm({ task_name: t.task_name, trade_id: t.trade_id || '', start_date: t.start_date, end_date: t.end_date, progress: t.progress, notes: t.notes || '' });
    setEditId(t.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/gantt-tasks/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/gantt/export/${format}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gantt_${projectId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error('Export failed'); }
  };

  // Gantt timeline calculation
  const { minDate, maxDate, totalDays } = useMemo(() => {
    if (tasks.length === 0) return { minDate: new Date(), maxDate: new Date(), totalDays: 30 };
    const dates = tasks.flatMap((t) => [new Date(t.start_date), new Date(t.end_date)]);
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    min.setDate(min.getDate() - 2);
    max.setDate(max.getDate() + 2);
    return { minDate: min, maxDate: max, totalDays: Math.max(7, daysBetween(min, max)) };
  }, [tasks]);

  const getBarStyle = (task) => {
    const start = new Date(task.start_date);
    const end = new Date(task.end_date);
    const left = (daysBetween(minDate, start) / totalDays) * 100;
    const width = (daysBetween(start, end) / totalDays) * 100;
    return { left: `${Math.max(0, left)}%`, width: `${Math.max(2, width)}%` };
  };

  const progressColors = ['bg-red-500', 'bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-yellow-400', 'bg-lime-400', 'bg-green-400', 'bg-green-500', 'bg-emerald-500', 'bg-emerald-600', 'bg-teal-600'];
  const getProgressColor = (p) => progressColors[Math.min(10, Math.floor(p / 10))];

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div>
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate(`/projects/${projectId}?tab=control`)} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={18} className="mr-1.5" /> Back to Control Tower
          </Button>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Gantt Chart</h1>
            <div className="flex gap-2">
              <Button onClick={() => handleExport('pdf')} variant="outline" className="border-zinc-300 rounded-none text-xs" data-testid="gantt-export-pdf"><FilePdf size={16} className="mr-1" /> PDF</Button>
              <Button onClick={() => handleExport('excel')} variant="outline" className="border-zinc-300 rounded-none text-xs" data-testid="gantt-export-excel"><FileXls size={16} className="mr-1" /> Excel</Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ModuleGuide text="Plan and visualise the execution timeline. Add trade-wise tasks with start/end dates and track progress. Export to PDF or Excel for reporting." />

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-zinc-500">{tasks.length} tasks</p>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="add-gantt-task"><Plus size={18} className="mr-1.5" /> Add Task</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl">
              <DialogHeader><DialogTitle className="text-xl font-bold">{editId ? 'Edit' : 'New'} Task</DialogTitle></DialogHeader>
              <form onSubmit={handleSave} className="space-y-3 mt-3">
                <div><Label className="text-xs font-bold uppercase text-zinc-500">Task Name *</Label><Input value={form.task_name} onChange={(e) => setForm({ ...form, task_name: e.target.value })} className="mt-1 rounded-none" data-testid="gantt-task-name" /></div>
                <div><Label className="text-xs font-bold uppercase text-zinc-500">Trade</Label>
                  <select value={form.trade_id} onChange={(e) => setForm({ ...form, trade_id: e.target.value })} className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="gantt-trade-select">
                    <option value="">None</option>
                    {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-bold uppercase text-zinc-500">Start Date *</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-none" data-testid="gantt-start" /></div>
                  <div><Label className="text-xs font-bold uppercase text-zinc-500">End Date *</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 rounded-none" data-testid="gantt-end" /></div>
                </div>
                <div><Label className="text-xs font-bold uppercase text-zinc-500">Progress: {form.progress}%</Label><input type="range" min="0" max="100" step="5" value={form.progress} onChange={(e) => setForm({ ...form, progress: parseInt(e.target.value) })} className="mt-1 w-full" data-testid="gantt-progress" /></div>
                <div><Label className="text-xs font-bold uppercase text-zinc-500">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-none" data-testid="gantt-notes" /></div>
                <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="gantt-save">{editId ? 'Update' : 'Create'} Task</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {tasks.length === 0 ? (
          <EmptyState
            title="No tasks yet"
            description="Add execution tasks with start and end dates to build a visual Gantt timeline for this project."
            testId="gantt-empty-state"
          />
        ) : (
          <div className="bg-white border border-zinc-200 overflow-hidden">
            {/* Timeline Header */}
            <div className="flex border-b border-zinc-200 bg-zinc-50">
              <div className="w-56 min-w-[224px] px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-zinc-500 border-r border-zinc-200">Task</div>
              <div className="flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-zinc-500 flex justify-between">
                <span>{minDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                <span>{maxDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
              </div>
            </div>
            {/* Task Rows */}
            {tasks.map((task) => {
              const barStyle = getBarStyle(task);
              const dprRef = dprTasks.find(d => d.id === task.id)?.dpr_reference;
              const hasDpr = dprRef && dprRef.entries > 0;
              return (
                <div key={task.id} className="flex border-b border-zinc-100 hover:bg-zinc-50 group" data-testid={`gantt-row-${task.id}`}>
                  <div className="w-56 min-w-[224px] px-4 py-3 border-r border-zinc-200 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{task.task_name}</p>
                      <p className="text-xs text-zinc-500">{task.trade_name || '—'} · {task.progress}%</p>
                      {hasDpr && (
                        <p className="text-[10px] text-blue-600 mt-0.5" data-testid={`gantt-dpr-ref-${task.id}`}>
                          DPR: {dprRef.dpr_pct}% ({dprRef.completed_qty}/{dprRef.total_qty})
                        </p>
                      )}
                    </div>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                      <button onClick={() => handleEdit(task)} className="p-1 text-zinc-400 hover:text-blue-700"><Pencil size={12} /></button>
                      <button onClick={() => handleDelete(task.id)} className="p-1 text-zinc-400 hover:text-red-700"><Trash size={12} /></button>
                    </div>
                  </div>
                  <div className="flex-1 relative py-3 px-2">
                    <div className="absolute h-6 rounded-sm top-1/2 -translate-y-1/2 bg-zinc-200" style={barStyle}>
                      <div className={`h-full rounded-sm ${getProgressColor(task.progress)}`} style={{ width: `${task.progress}%` }} />
                      {hasDpr && (
                        <div className="absolute top-0 left-0 h-full border-r-2 border-blue-500 border-dashed" style={{ width: `${dprRef.dpr_pct}%` }} title={`DPR Reference: ${dprRef.dpr_pct}%`} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};
